import * as R from 'ramda';
import { EPIC_LEVEL } from '@atlassian/jira-portfolio-3-common';
import {
	getEstimateValue,
	getTimeSpentValue,
	isEstimated,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/estimation';
import type { IssueStatusesById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-statuses/types';
import type { IssueType } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-types';
import type { Issue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues';
import type {
	IssuesStatusBreakdown,
	IssuesBreakdownByEstimate,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues/types';
import type { ScopeIssue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/scope/types';
import type { Project } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/projects/types';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import {
	ISSUE_STATUS_CATEGORIES_CONST,
	PlanningUnits,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';

export const getIssuesStatusBreakdown = (
	issues: Issue[],
	issueStatuses: IssueStatusesById,
	option?: {
		planningUnit: string;
		workingHours: number;
	},
): IssuesStatusBreakdown => {
	const { TODO, INPROGRESS, DONE } = ISSUE_STATUS_CATEGORIES_CONST;
	const categoryKeys = [TODO, INPROGRESS, DONE].map(String);

	const getCategoryId = ({ status }: { status?: string }) => {
		const categoryId = R.path([status || '', 'categoryId'], issueStatuses);
		if (!isDefined(categoryId)) throw new Error(`Unexpected issue status: ${status || ''}`);
		return String(categoryId);
	};

	/**
	 * Groups the array of Issue[] by their categoryId
	 * Input: Issue[],
	 * Output:  {
	 *  [TODO]: Issue[], [INPROGRESS]: Issue[], [DONE]: Issue[]
	 * }
	 */
	const groupedByCategoryId = R.compose<
		Issue[],
		Issue[],
		{ [TODO]?: Issue[]; [DONE]?: Issue[]; [INPROGRESS]?: Issue[] },
		{ [TODO]?: Issue[]; [DONE]?: Issue[]; [INPROGRESS]?: Issue[] },
		{ [TODO]: Issue[]; [DONE]: Issue[]; [INPROGRESS]: Issue[] }
	>(
		R.merge({ [TODO]: [], [INPROGRESS]: [], [DONE]: [] }),
		R.pick(categoryKeys),
		R.groupBy(getCategoryId),
		R.filter(({ status }: Issue) => isDefined(status)),
	)(issues);

	/**
	 * Counts the number for each category, the calculation is different depending on planningUnit
	 * Output: { [TODO]: number, [INPROGRESS]: number, [DONE]: number }
	 */
	const byCategoryId = !option
		? // eslint-disable-next-line @typescript-eslint/no-shadow
			R.mapObjIndexed((issues) => issues.length, groupedByCategoryId)
		: R.mapObjIndexed(
				// eslint-disable-next-line @typescript-eslint/no-shadow
				(issues) =>
					issues.reduce((total, issue) => {
						const { storyPoints } = issue;
						const { planningUnit, workingHours } = option;
						switch (planningUnit) {
							case PlanningUnits.days:
							case PlanningUnits.hours:
								return (
									total +
									// TODO: Current behaviour is to ignore timeSpent, but it doesn't really make sense
									// getTimeSpentValue(planningUnit, issue, workingHours) +
									getEstimateValue(planningUnit, issue, workingHours)
								);
							case PlanningUnits.storyPoints:
								return total + (storyPoints ?? 0);
							default:
								return total + 1;
						}
					}, 0),
				groupedByCategoryId,
			);

	/**
	 * Adds all the values in the object.
	 * Input: { [any]: number }, Output: number
	 */
	const total = R.compose<{ [status: number]: number }, number[], number>(
		R.sum,
		R.values,
	)(byCategoryId);

	return {
		byCategoryId,
		total,
	};
};

/**
 * Distribute estimation of issues by their categories.
 * Scenario issues doesn't have status and are intentionally ignored.
 */
export const getEstimateBreakdown = (
	issue: Issue | ScopeIssue,
	childIssues: Issue[],
	issueStatuses: IssueStatusesById,
	planningUnit: string,
	workingHours: number,
): IssuesBreakdownByEstimate => {
	const { TODO, INPROGRESS, DONE } = ISSUE_STATUS_CATEGORIES_CONST;
	const breakdown = {
		byCategoryId: {
			[TODO]: 0,
			[INPROGRESS]: 0,
			[DONE]: 0,
		},
		total: 0,
		unestimated: 0,
		issueCount: 0,
	};
	// eslint-disable-next-line @typescript-eslint/no-shadow
	const updateBreakdown = (issue: Issue | ScopeIssue) => {
		if (isEstimated(planningUnit, issue)) {
			// If the issue is scenario issue, there won't be an issue status associated so consider them as TODO.
			const categoryId: typeof TODO | typeof INPROGRESS | typeof DONE =
				R.path([issue.status || '', 'categoryId'], issueStatuses) || TODO;
			const remainingEstimate = getEstimateValue(planningUnit, issue, workingHours);
			const timeSpent = getTimeSpentValue(planningUnit, issue, workingHours);

			if (categoryId === DONE) {
				// Done should include both the time spent and the remaining time for the issue.
				breakdown.byCategoryId[categoryId] =
					(breakdown.byCategoryId[categoryId] || 0) + remainingEstimate + timeSpent;
			} else {
				// Inprogress and todo should include the remaining estimate only.
				// Time spent for these statuses will be attributed to 'done'.
				breakdown.byCategoryId[categoryId] =
					(breakdown.byCategoryId[categoryId] || 0) + remainingEstimate;
				breakdown.byCategoryId[DONE] = (breakdown.byCategoryId[DONE] || 0) + timeSpent;
			}
			breakdown.total += remainingEstimate + timeSpent;

			breakdown.issueCount += 1;
		} else {
			breakdown.unestimated += 1;
		}
	};

	childIssues.forEach(updateBreakdown);
	updateBreakdown(issue);

	return breakdown;
};

export const isIssueSubTaskType = ({
	issue,
	issueTypesById,
}: {
	issue: ScopeIssue;
	issueTypesById: Record<number | string, IssueType>;
}): boolean => {
	const { type } = issue;
	const issueType = issueTypesById[type];
	return issueType && !!issueType.subTask;
};

export const getPossibleParentIssues = ({
	issue,
	issues,
	issueTypesById,
	projectsById,
}: {
	issue: ScopeIssue;
	issues: Issue[];
	issueTypesById: Record<number | string, IssueType>;
	projectsById: Record<number | string, Project>;
}): Issue[] => {
	const { level: issueLevel, project: issueProjectId } = issue;
	const isIssueSubtask = isIssueSubTaskType({ issue, issueTypesById });

	const { isSimplified: isIssueSimplified } = projectsById[issueProjectId] ?? {
		isSimplified: false,
	};

	const isIssueLevelOne = issueLevel === EPIC_LEVEL;

	return issues.filter((currentIssue) => {
		if (isIssueSimplified) {
			return (
				// TMP only allows reparent within project scope except for level one to level two (Epic to Initiative)
				currentIssue.level === issueLevel + 1 &&
				(currentIssue.project === issueProjectId || isIssueLevelOne)
			);
		}
		const { isSimplified: isCurrentIssueSimplified } = projectsById[currentIssue.project] ?? {
			isSimplified: false,
		};

		if (isCurrentIssueSimplified) {
			// Jira doesn't allow cross project-type reparenting
			return false;
		}

		return isIssueSubtask
			? /**
				 * [This case applies to CMP only]
				 * Allow reparenting subtask to issue of any non subtask-level
				 */
				currentIssue.level !== issueLevel
			: currentIssue.level === issueLevel + 1;
	});
};
